/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-explicit-any */
type Fn<In, Out> = (x: In) => Out;

class Chainable<T> {
  constructor (private _value: T) {}

  get value () { return this._value }

  chain (): Chainable<T>;
  chain<A1>(fn1: Fn<T, A1>): Chainable<A1>;
  chain<A1, A2>(fn1: Fn<T, A1>, fn2: Fn<A1, A2>): Chainable<A2>;
  chain<A1, A2, A3>(fn1: Fn<T, A1>, fn2: Fn<A1, A2>, fn3: Fn<A2, A3>): Chainable<A3>;
  chain<A1, A2, A3, A4>(fn1: Fn<T, A1>, fn2: Fn<A1, A2>, fn3: Fn<A2, A3>, fn4: Fn<A3, A4>): Chainable<A4>;
  chain<A1, A2, A3, A4, A5>(fn1: Fn<T, A1>, fn2: Fn<A1, A2>, fn3: Fn<A2, A3>, fn4: Fn<A3, A4>, fn5: Fn<A4, A5>): Chainable<A5>;
  chain<A1, A2, A3, A4, A5, A6>(fn1: Fn<T, A1>, fn2: Fn<A1, A2>, fn3: Fn<A2, A3>, fn4: Fn<A3, A4>, fn5: Fn<A4, A5>, fn6: Fn<A5, A6>): Chainable<A6>;
  chain<A1, A2, A3, A4, A5, A6, A7>(fn1: Fn<T, A1>, fn2: Fn<A1, A2>, fn3: Fn<A2, A3>, fn4: Fn<A3, A4>, fn5: Fn<A4, A5>, fn6: Fn<A5, A6>, fn7: Fn<A6, A7>): Chainable<A7>;
  chain<A1, A2, A3, A4, A5, A6, A7, A8>(fn1: Fn<T, A1>, fn2: Fn<A1, A2>, fn3: Fn<A2, A3>, fn4: Fn<A3, A4>, fn5: Fn<A4, A5>, fn6: Fn<A5, A6>, fn7: Fn<A6, A7>, fn8: Fn<A7, A8>): Chainable<A8>;
  chain<A1, A2, A3, A4, A5, A6, A7, A8, A9>(fn1: Fn<T, A1>, fn2: Fn<A1, A2>, fn3: Fn<A2, A3>, fn4: Fn<A3, A4>, fn5: Fn<A4, A5>, fn6: Fn<A5, A6>, fn7: Fn<A6, A7>, fn8: Fn<A7, A8>, fn9: Fn<A8, A9>): Chainable<A9>;
  chain<A1, A2, A3, A4, A5, A6, A7, A8, A9>(fn1: Fn<T, A1>, fn2: Fn<A1, A2>, fn3: Fn<A2, A3>, fn4: Fn<A3, A4>, fn5: Fn<A4, A5>, fn6: Fn<A5, A6>, fn7: Fn<A6, A7>, fn8: Fn<A7, A8>, fn9: Fn<A8, A9>, ...fns: Fn<any, any>[]): Chainable<unknown>;
  chain (...fns: Fn<any, any>[]): Chainable<any> {
    if (fns.length === 0) return this;
    return new Chainable(fns.reduce((prev, fn) => fn(prev), this.value));
  }
}

/**
 * Wraps a value in a Chainable to build a strongly-typed function pipeline with .chain().
 * Each chained function is applied in order, and the final result is available via .value.
 *
 * @param x - The value to wrap.
 * @returns A Chainable<T> around the given value.
 *
 * @example
 * const result = makeChainable([1, 2, 3])
 *   .chain(
 *     xs => xs.map(x => x * 2)),                // [2, 4, 6]
 *     xs => xs.reduce((acc, x) => acc + x, 0)   // 12
 *   );
 *
 * console.log(result.value); // 12
 */
export function makeChainable<T> (x: T) { return new Chainable(x) }
